import { Injectable } from '@angular/core';
import { InternalDatabaseService } from '../internal/internal-database.service';
import { Doctor } from './doctor';
import { IDoctor } from '@stockcare-models/IDoctor';
import { Observable } from 'rxjs';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';

@Injectable({
  providedIn: 'root',
})
export class DoctorStorageService {
  readonly table: string;
  readonly Index: string;
  enableLocaldb: boolean;
  doctors: Doctor[] = [];

  constructor(private internalDatabaseService: InternalDatabaseService, private readonly settingsService: SettingsService) {
    const table = Doctor.getTableDefinition();
    this.table = table.TableName;
    this.Index = table.Indexes[0].Name;
  }

  doctorsLoaded(): boolean {
    if (this.enableLocaldb) {
      return true;
    }

    return this.doctors.length > 0;
  }

  addDoctors(doctors: Doctor[]) {
    this.getSettings();

    return new Observable((observer) => {
      this.getAllDoctors().subscribe((existingDoctors) => {
        if (!this.enableLocaldb) {
          this.doctors = [...doctors];
          observer.next();
        } else {
          const doctorsToAdd: Doctor[] = [];
          doctors.forEach((x) => {
            const existingDoctor = existingDoctors.find((doctor) => doctor.ReferenceId === x.ReferenceId);

            if (!existingDoctor) {
              doctorsToAdd.push(x);
            }
          });

          this.internalDatabaseService.addRange(this.table, doctorsToAdd).then(() => {
            observer.next();
          });
        }
      });
    });
  }

  getSettings() {
    this.enableLocaldb = this.settingsService.getBooleanSetting(Settings.ENABLE_INDEXED_DB);
  }

  async addPatient(doctor: Doctor) {
    await this.internalDatabaseService.add(this.table, doctor);
  }

  getAllDoctors(): Observable<Doctor[]> {
    return new Observable((observer) => {
      if (!this.enableLocaldb) {
        observer.next(this.doctors);
      } else {
        this.internalDatabaseService.getAll<Doctor>(this.table).then((doctors) => {
          observer.next(doctors);
        });
      }
    });
  }

  async getDoctorsByIds(doctorIds: number[]): Promise<Doctor[]> {
    const doctors: Doctor[] = [];

    return new Promise((resolve, reject) => {
      if (doctorIds.length > 0) {
        if (!this.enableLocaldb) {
          doctorIds.forEach(async (x, index, array) => {
            let foundDoctor = this.doctors.find((doctor) => doctor.Id === x);

            if (foundDoctor) {
              doctors.push(foundDoctor);
            }

            if (index === array.length - 1) resolve(doctors);
          });
        } else {
          doctorIds.forEach(async (x, index, array) => {
            const doctor = await this.internalDatabaseService.getByIndex<Doctor>(this.table, this.Index, x);
            doctors.push(doctor);
            if (index === array.length - 1) resolve(doctors);
          });
        }
      } else {
        resolve(doctors);
      }
    });
  }

  async getDoctorsByRange(): Promise<Doctor[]> {
    if (!this.enableLocaldb) {
      return new Promise<Doctor[]>((resolve, reject) => resolve(this.doctors.slice(0, 21)));
    } else {
      var keyRangeValue = IDBKeyRange.bound(0, 20, true, true);
      return await this.internalDatabaseService.getByRange<Doctor>(this.table, keyRangeValue);
    }
  }

  async getAnaesthetistsByIds(anaesthetistIds: number[]): Promise<Doctor[]> {
    return await this.getDoctorsByIds(anaesthetistIds);
  }
  map(doctors: IDoctor[]) {
    const savedDoctors: Doctor[] = [];

    for (const doctor of doctors) {
      const mappedDoctor = new Doctor();
      mappedDoctor.Id = doctor.id;
      mappedDoctor.ReferenceId = doctor.referenceId;
      mappedDoctor.Name = doctor.name;
      mappedDoctor.Title = doctor.title;
      mappedDoctor.TelephoneNumber = doctor.telephoneNumber;
      mappedDoctor.PracticeNumber = doctor.practiceNumber;
      savedDoctors.push(mappedDoctor);
    }

    return savedDoctors;
  }
}
